import React, { ChangeEvent } from "react";
import { Box, FormControlLabel, Grid, InputAdornment } from "@mui/material";
import OrderedList from "./OrderedList";
import { DropResult } from "@edorivai/react-smooth-dnd";
import produce from "immer";
import { startCase } from "lodash";
import { Data, SurchargePercentage } from "../store/terminal/types";
import CustomizedSwitch from "./formElements/Switch";
import pageStyles from "./../pages/pageStyles";
import { FormikErrors, useFormikContext, FormikProps } from "formik";
import SectionWrapper from "./pageSections/SectionWrapper";
import OptionalToolTip from "./toolTip/OptionalToolTip";
import TextInput from "./formElements/TextInput";
import { ConfigPropExtenderHoc } from "./StaticConfigProvider";
import { connect, useSelector } from "react-redux";
import SelectDropDown from "./formElements/SelectDropDown";
import { ApplicationState } from "../store";

let cardTypeOptionsState: any = [];

interface ItemProps {
  surcharge: SurchargePercentage;
  index: number;
  onChange: (
    attribute: string
  ) => (event: ChangeEvent<{ value: unknown }>) => void;
  values?: any;
  errors?: FormikErrors<Data>;
  disabled?: boolean;
}

const SurchargeListItem = ({ index, disabled }: ItemProps) => {
  return (
    <Grid container xs={12} md={12} spacing={2}>
      <Grid item xs={6} md={8}>
        <SelectDropDown
          label="Card Type"
          fieldname={`attributes.SurchargePercentage[${index}].CardType`}
          options={cardTypeOptionsState}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={3} md={4}>
        <TextInput
          fieldname={`attributes.SurchargePercentage[${index}].Percentage`}
          label="Surcharge"
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

interface SurchargeProps {
  toolTips?: { terminals?: { options?: Record<string, string> } };
  isUpdateAllowed?: boolean;
}

const Surcharge = (props: SurchargeProps) => {
  const stateLoading = useSelector((state: ApplicationState) => state.cardType);
  const loading = stateLoading.loading;

  const stateData: any = useSelector(
    (state: ApplicationState) => state.cardType.data?.data
  ) ?? [{}];

  if (!loading) {
    cardTypeOptionsState = stateData.map((item: any) => {
      return { [item.type]: startCase(item.type.toLowerCase()) };
    });
  }

  const formProps = useFormikContext<FormikProps<Data>>();
  const { setFieldValue, errors } = formProps;
  const values = formProps.values as unknown as Data;
  const classes = pageStyles();
  const { isUpdateAllowed } = props;

  const {
    SurchargePercentage,
    FixedSurchargeAmount,
    FixedSurchargePercentage,
  } = values.attributes;

  const isDisableFixedSurchargePercentage = Boolean(FixedSurchargeAmount);
  const isDisableFixedSurchargeAmount = Boolean(FixedSurchargePercentage);
  const isDisableSurcharge = Boolean(
    FixedSurchargeAmount || FixedSurchargePercentage
  );

  const changeListenerBuilder =
    (index: number, setFieldValue: (field: string, value: any) => void) =>
    (attributeName: string) =>
    (event: ChangeEvent<{ value: unknown }>) => {
      if (setFieldValue && index > -1) {
        const newItems = produce(
          SurchargePercentage,
          (draft: SurchargePercentage[]) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            draft[index][attributeName] = event.target.value;
          }
        );
        setFieldValue("attributes.SurchargePercentage", newItems);
      }
    };

  const onDrop = ({ removedIndex, addedIndex }: DropResult) => {
    if (setFieldValue && removedIndex !== null && addedIndex !== null) {
      const newItems = produce(
        SurchargePercentage,
        (draft: SurchargePercentage[]) => {
          const items = draft.splice(removedIndex, 1);
          draft.splice(addedIndex, 0, ...items);
        }
      );
      setFieldValue("attributes.SurchargePercentage", newItems);
    }
  };

  const onAdd = () => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargePercentage",
        produce(SurchargePercentage, (draft: SurchargePercentage[]) => {
          draft.push({ SurchargeGST: 0, Percentage: 0, CardType: "" });
        })
      );
    }
  };

  const onDelete = (index: number) => {
    if (setFieldValue) {
      setFieldValue(
        "attributes.SurchargePercentage",
        produce(SurchargePercentage, (draft: SurchargePercentage[]) => {
          draft.splice(index, 1);
        })
      );
    }
  };

  return (
    <>
      <SectionWrapper isChild={true} title="Surcharge">
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12}>
            <Box p={2} pt={0}>
              <FormControlLabel
                style={{ marginRight: "3rem" }}
                control={
                  <CustomizedSwitch
                    checked={values.attributes.Surcharge as boolean}
                    onChange={(event) => {
                      setFieldValue(
                        "attributes.Surcharge",
                        event.target.checked
                      );
                    }}
                    disabled={!isUpdateAllowed}
                  />
                }
                label="Surcharge"
              />
              <FormControlLabel
                style={{ marginRight: "3rem" }}
                control={
                  <CustomizedSwitch
                    checked={values.attributes.SurchargeTip as boolean}
                    onChange={(event) => {
                      setFieldValue(
                        "attributes.SurchargeTip",
                        event.target.checked
                      );
                    }}
                    disabled={!isUpdateAllowed}
                  />
                }
                label="Surcharge on tips"
              />
            </Box>

            <hr className={classes.devide} />
          </Grid>
          <Grid item xs={12} md={9}>
            <h5 style={{ marginTop: 0 }}>Card types</h5>
            {
              <OrderedList<SurchargePercentage>
                key={`surcharge-list-${Boolean(
                  isDisableSurcharge || !isUpdateAllowed
                )}`}
                items={SurchargePercentage || []}
                itemComponent={(item, index) => (
                  <SurchargeListItem
                    key={`surcharge-${index}-${Boolean(
                      isDisableSurcharge || !isUpdateAllowed
                    )}`}
                    index={index}
                    surcharge={item}
                    values={values}
                    errors={errors as FormikErrors<Data>}
                    onChange={changeListenerBuilder(index, setFieldValue)}
                    disabled={isDisableSurcharge || !isUpdateAllowed}
                  />
                )}
                onAdd={onAdd}
                onDelete={onDelete}
                onDrop={onDrop}
                emptyMessage="No card types defined"
                disabled={isDisableSurcharge || !isUpdateAllowed}
              />
            }
          </Grid>
        </Grid>
      </SectionWrapper>
      <SectionWrapper isChild={true} title="Fixed Surcharge Rate">
        <Grid container spacing={2} xs={12}>
          <Grid item xs={12} md={3}>
            <OptionalToolTip tooltip="Adds a percentage surcharge to each transaction across all card types">
              <TextInput
                key={`FixedSurchargePercentage+${isDisableFixedSurchargePercentage}`}
                type="number"
                fieldname="attributes.FixedSurchargePercentage"
                label="Percentage"
                disabled={isDisableFixedSurchargePercentage || !isUpdateAllowed}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </OptionalToolTip>
          </Grid>
          <Grid item xs={12} md={3}>
            <OptionalToolTip tooltip="Adds a fixed surcharge rate to each transaction across all card types">
              <TextInput
                key={`FixedSurchargeAmount+${isDisableFixedSurchargeAmount}`}
                type="number"
                fieldname="attributes.FixedSurchargeAmount"
                label="Amount"
                disabled={isDisableFixedSurchargeAmount || !isUpdateAllowed}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">$</InputAdornment>
                  ),
                }}
              />
            </OptionalToolTip>
          </Grid>
        </Grid>
      </SectionWrapper>
    </>
  );
};

const ConnectedSurcharge = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: SurchargeProps) => {
    return {
      ...ownProps,
    };
  }
)(Surcharge);

const ConnectedSurchargeHoc = (props: SurchargeProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedSurcharge {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedSurchargeHoc;
